import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { Button } from '../jsxElements';
import IconButton from '@mui/material/IconButton';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { logEvent } from '../analytics';
import styles from './Timeslots.module.css';
import sharedStyles from '../shared.module.css';

import { AmpFlagSet } from '../amplitude';

export interface TimeslotAttachmentData {
    days: any[];
    providerName: string;
    removeBubble: boolean;
    hideAvatar: boolean;
}

type ProvidenceTimeslotsArgs = {
    data: TimeslotAttachmentData;
    disableButtons: boolean;
    featureFlags: AmpFlagSet;
    sendMessage: (message: string) => void;
};

function renderTimeslotRow(
    index: string,
    day: string,
    times: string[],
    handleButtonClick: (time: string, key: string) => void,
    disableButtons: boolean,
    selectedButtonKey: string,
    featureFlags: AmpFlagSet
) {
    const singleRow = times.map((time) => {
        const key = `idx-${time}-i`;
        return (
            <Button
                key={key}
                disableChildMargin={true}
                className={styles.timeslotsButton}
                button={{ title: time }}
                disableButtons={disableButtons}
                featureFlags={featureFlags}
                selected={key === selectedButtonKey}
                onClick={() => {
                    handleButtonClick(time, key);
                }}
            />
        );
    });
    return (
        <div className={styles.timeslotsButtonRow} key={index}>
            {singleRow}
        </div>
    );
}

export const Timeslots: React.FC<ProvidenceTimeslotsArgs> = ({
    data,
    disableButtons,
    featureFlags,
    sendMessage,
}: ProvidenceTimeslotsArgs) => {
    const [numberRows, setNumberRows] = useState(1);
    const [currentDay, setCurrentDay] = useState('');
    const [selectedButtonKey, setSelectedButtonKey] = useState('');

    useEffect(() => {
        setCurrentDay(data.days[0].day);
    }, [data]);

    const selectedDayIndex = (data.days || []).findIndex(
        (day: any) => day.day === currentDay
    );
    const selectedDayObject = (data.days || []).find(
        (day: any) => day.day === currentDay
    );

    const times = (selectedDayObject ? selectedDayObject.times : []).map(
        (time: any) => time.time
    );

    const handleButtonClick = (time: string, key = '') => {
        if (time === 'Return to main menu') {
            setSelectedButtonKey(time);
            sendMessage(time);
        } else {
            setSelectedButtonKey(key);
            sendMessage(currentDay + ', ' + time);
        }
    };

    const hasNextDay = selectedDayIndex < data.days.length - 1;
    const hasPreviousDay = selectedDayIndex > 0;

    const availableRows = Math.ceil(times.length / 3);
    const rows = [];
    let row = [];
    for (let i = 0; i < times.length; i++) {
        row.push(times[i]);
        if (row.length === 3 || (row.length && times.length - 1 === i)) {
            rows.push(
                renderTimeslotRow(
                    `idx-${i}`,
                    currentDay,
                    row,
                    handleButtonClick,
                    disableButtons,
                    selectedButtonKey,
                    featureFlags
                )
            );
            row = [];
        }
        if (rows.length >= numberRows) {
            break;
        }
    }

    const setPreviousDay = () => {
        setNumberRows(1);
        setCurrentDay(data.days[selectedDayIndex - 1].day);
        logEvent('PC - Date picker');
    };
    const setNextDay = () => {
        setNumberRows(1);
        setCurrentDay(data.days[selectedDayIndex + 1].day);
        logEvent('PC - Date picker');
    };
    const showMoreButtonClick = () => {
        logEvent('PC - Show more times');
        setNumberRows(availableRows);
    };

    const firstTime = times.length > 0 ? times[0] : '';
    let dayNoYear = currentDay;
    if (dayNoYear.indexOf(',') > 0) {
        dayNoYear = dayNoYear.split(',')[0];
    }
    const bookingMessage = `${data.providerName} is available on ${dayNoYear} starting at ${firstTime}. Please select the time you'd like for your appointment.`;

    const navigationButtonDisabled = disableButtons
        ? styles.timeslotsNavigationButtonDisabled
        : null;

    return (
        <div className={sharedStyles.botMessageNoBubble}>
            <div className={styles.timeslotsTitleRow}>
                <div className={styles.timeslotsArrowIcon}>
                    {hasPreviousDay && (
                        <IconButton
                            onClick={setPreviousDay}
                            disabled={disableButtons}
                            size="small"
                            className={classNames(
                                styles.timeslotsNavigationButton,
                                navigationButtonDisabled
                            )}
                        >
                            <ArrowBackIosIcon fontSize="small" />
                        </IconButton>
                    )}
                </div>
                <span className={styles.timeslotsTitle}>{currentDay}</span>
                <div className={styles.timeslotsArrowIcon}>
                    {hasNextDay && (
                        <IconButton
                            onClick={setNextDay}
                            disabled={disableButtons}
                            size="small"
                            className={classNames(
                                styles.timeslotsNavigationButton,
                                navigationButtonDisabled
                            )}
                        >
                            <ArrowForwardIosIcon fontSize="small" />
                        </IconButton>
                    )}
                </div>
            </div>
            <p>{bookingMessage}</p>
            {rows}
            {numberRows < availableRows && (
                <Button
                    button={{ title: 'Show More' }}
                    className={styles.timeslotsShowMoreButton}
                    featureFlags={featureFlags}
                    onClick={showMoreButtonClick}
                    disableButtons={disableButtons}
                />
            )}
            {!hasNextDay && numberRows >= availableRows && (
                <div className={styles.timeslotsMoreOptions}>
                    <p>
                        Can’t find the right date and time? Call your provider
                        for more options.
                    </p>
                    <Button
                        button={{ title: 'Return to main menu' }}
                        disableButtons={disableButtons}
                        featureFlags={featureFlags}
                        selected={'Return to main menu' === selectedButtonKey}
                        onClick={() => {
                            handleButtonClick('Return to main menu');
                        }}
                    />
                </div>
            )}
        </div>
    );
};
